import {
  integer, maxLength, minLength, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import { atLeastOneSegmentValidator, includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import type {
  FlowOperatorMetadata,
  ValidationResult,
  NumberRange,
  Period,
  PickerData,
} from '@/types/flow-types';
import Form from './Form.vue';

export interface CurrentCartFilters {
  filterproductinbag?: {
    segment_number: number;
    product_picker_bag: PickerData;
    litteral_title: StringMap;
  };
  filterproductcategoryinbag?: {
    segment_number: number;
    product_picker_category_bag: PickerData;
    litteral_title: StringMap;
  };
  filterproductmanufacturerinbag?: {
    segment_number: number;
    product_picker_manufacturer_bag: PickerData;
    litteral_title: StringMap;
  };
  filtercartamount?: {
    segment_number: number;
    cart_amount_currency: NumberRange;
    litteral_title: StringMap;
  };
}

export interface CurrentCartData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null | number;
  grant_additional_delay: string;
  extra_time: Period;
  inclusion: CurrentCartFilters;
  exclusion: CurrentCartFilters;
}

export function createDefaultData(data: Partial<CurrentCartData> = {}): CurrentCartData {
  return {
    custom_title: data.custom_title ?? '',
    custom_description: data.custom_description ?? '',
    outputs_number: data.outputs_number ?? 1,
    initial_number_outputs: data.initial_number_outputs,
    grant_additional_delay: data.grant_additional_delay ?? '0',
    extra_time: data.extra_time ?? { value: 1, unit: 'DAY' },
    inclusion: data.inclusion ?? {},
    exclusion: data.exclusion ?? {},
  };
}

const CurrentCart: FlowOperatorMetadata<CurrentCartData> = {
  id: 'boxfiltre_1',
  kind: 'filter',
  label: 'automatedScenarios.filters.currentCart',
  icon: 'fa-cogs',
  hasConfiguration: true,
  // Deprecated: replaced by ContactSegmentation ("Conditions logiques").
  // Metadata kept registered so existing flows still render and validate.
  availableIn: [],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching', 'boxfiltre_2'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxfiltre_2'],
    },
  },

  dependencies: {
    [TypeCampaignEnum.AUTOMATION]: {
      operators: {
        any_parents: [
          'boxabandonpanier',
          'boxscrollpage',
          'boxvisitpage',
          'boxcustomjs',
          'boxsitepasstime',
          'registeronsite',
          'subscribetonewsletter',
          { boxId: 'boxquitsite', attribute: 'multiplebox_type', value: '1' },
        ],
      },
    },
  },

  persistent: [
    { segmentId: 'filterproductinbag', boxId: 'boxstatuscommand' },
    { segmentId: 'filterproductcategoryinbag', boxId: 'boxstatuscommand' },
    { segmentId: 'filterproductmanufacturerinbag', boxId: 'boxstatuscommand' },
    { segmentId: 'filtercartamount', boxId: 'boxstatuscommand' },
  ],

  Form,

  defaultData: createDefaultData,

  outputs: (data: CurrentCartData) => data.outputs_number,

  getDisplayLabel(data: CurrentCartData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: CurrentCartData): string | null {
    return data.custom_description || null;
  },

  async validate(data: CurrentCartData): Promise<ValidationResult> {
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = {
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      inclusion: {
        required: atLeastOneSegmentValidator(data.exclusion),
      },
    };

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: CurrentCartData): CurrentCartData {
    const dataToReturn = { ...data };
    delete dataToReturn.initial_number_outputs;
    return dataToReturn;
  },

  canBeDeleted(): boolean {
    return true;
  },
};

export default CurrentCart;
